import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { receitasAPI, clientesAPI, produtosAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Save, FileText, Plus, Trash2, Search } from 'lucide-react';
import Swal from 'sweetalert2';

export const ReceitaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  usePageTitle(isEdit ? 'Editar Receita' : 'Nova Receita');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  
  // Cliente search
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteResults, setClienteResults] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  
  // Produto search
  const [produtoSearch, setProdutoSearch] = useState('');
  const [produtoResults, setProdutoResults] = useState([]);
  const [showProdutoDropdown, setShowProdutoDropdown] = useState(false);
  
  // Form data
  const [produtos, setProdutos] = useState([]);
  const [observacoesInternas, setObservacoesInternas] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetchReceita();
    }
  }, [id]);

  const fetchReceita = async () => {
    try {
      const response = await receitasAPI.getOne(id);
      const receita = response.data;
      
      setSelectedCliente({
        id: receita.cliente_id,
        nome: receita.cliente_nome
      });
      setClienteSearch(receita.cliente_nome);
      setProdutos(receita.produtos || []);
      setObservacoesInternas(receita.observacoes_internas || '');
    } catch (error) {
      console.error('Error fetching receita:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Receita não encontrada.',
        confirmButtonColor: '#0D9488'
      });
      navigate('/receitas');
    } finally {
      setFetching(false);
    }
  };

  // Debounced cliente search
  const searchClientes = useCallback(async (query) => {
    if (query.length < 2) {
      setClienteResults([]);
      return;
    }
    try {
      const response = await clientesAPI.search(query);
      setClienteResults(response.data);
    } catch (error) {
      console.error('Error searching clientes:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (clienteSearch && !selectedCliente) {
        searchClientes(clienteSearch);
        setShowClienteDropdown(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clienteSearch, selectedCliente, searchClientes]);

  // Debounced produto search
  const searchProdutos = useCallback(async (query) => {
    if (query.length < 2) {
      setProdutoResults([]);
      return;
    }
    try {
      const response = await produtosAPI.search(query);
      setProdutoResults(response.data);
    } catch (error) {
      console.error('Error searching produtos:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (produtoSearch) {
        searchProdutos(produtoSearch);
        setShowProdutoDropdown(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [produtoSearch, searchProdutos]);

  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente);
    setClienteSearch(cliente.nome);
    setShowClienteDropdown(false);
    setClienteResults([]);
  };

  const handleAddProduto = (produto) => {
    // Check if product already exists
    if (produtos.some(p => p.produto_id === produto.id)) {
      Swal.fire({
        icon: 'warning',
        title: 'Produto já adicionado',
        text: 'Este produto já está na receita.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    setProdutos([...produtos, {
      produto_id: produto.id,
      produto_nome: produto.nome,
      quantidade: 1,
      observacao: produto.observacao_padrao || ''
    }]);
    setProdutoSearch('');
    setShowProdutoDropdown(false);
    setProdutoResults([]);
  };

  const handleUpdateProduto = (index, field, value) => {
    const updated = [...produtos];
    updated[index] = {
      ...updated[index],
      [field]: field === 'quantidade' ? parseInt(value) || 1 : value
    };
    setProdutos(updated);
  };

  const handleRemoveProduto = (index) => {
    setProdutos(produtos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCliente) {
      Swal.fire({
        icon: 'warning',
        title: 'Cliente obrigatório',
        text: 'Selecione um cliente para a receita.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    if (produtos.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Produtos obrigatórios',
        text: 'Adicione pelo menos um produto à receita.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    setLoading(true);
    try {
      const data = {
        cliente_id: selectedCliente.id,
        cliente_nome: selectedCliente.nome,
        produtos,
        observacoes_internas: observacoesInternas
      };

      if (isEdit) {
        await receitasAPI.update(id, data);
        Swal.fire({
          icon: 'success',
          title: 'Atualizada!',
          text: 'Receita atualizada com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        await receitasAPI.create(data);
        Swal.fire({
          icon: 'success',
          title: 'Criada!',
          text: 'Receita criada com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      }
      navigate('/receitas');
    } catch (error) {
      console.error('Error saving receita:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível guardar a receita.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="receita-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/receitas">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            {isEdit ? 'Editar Receita' : 'Nova Receita'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? 'Atualize os dados da receita' : 'Crie uma nova receita médica'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Cliente Section */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-600" />
              Dados do Cliente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 relative">
              <Label htmlFor="cliente" className="text-slate-700">
                Cliente <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="cliente"
                  value={clienteSearch}
                  onChange={(e) => {
                    setClienteSearch(e.target.value);
                    if (selectedCliente) {
                      setSelectedCliente(null);
                    }
                  }}
                  placeholder="Pesquise pelo nome ou ID especial do cliente..."
                  className="pl-10"
                  data-testid="receita-cliente-search"
                />
              </div>
              
              {/* Cliente Dropdown */}
              {showClienteDropdown && clienteResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {clienteResults.map((cliente) => (
                    <button
                      key={cliente.id}
                      type="button"
                      onClick={() => handleSelectCliente(cliente)}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      data-testid={`cliente-option-${cliente.id}`}
                    >
                      <p className="font-medium text-slate-900">{cliente.nome}</p>
                      <p className="text-sm text-slate-500">
                        {cliente.id_especial && `ID: ${cliente.id_especial} • `}
                        {cliente.nif && `NIF: ${cliente.nif}`}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              
              {selectedCliente && (
                <p className="text-sm text-amber-600 mt-2">
                  Cliente selecionado: <span className="font-medium">{selectedCliente.nome}</span>
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Produtos Section */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900">
              Produtos / Medicamentos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add Produto */}
            <div className="relative">
              <Label className="text-slate-700 mb-2 block">Adicionar Produto</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={produtoSearch}
                  onChange={(e) => setProdutoSearch(e.target.value)}
                  placeholder="Pesquise pelo nome do produto..."
                  className="pl-10"
                  data-testid="receita-produto-search"
                />
              </div>
              
              {/* Produto Dropdown */}
              {showProdutoDropdown && produtoResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {produtoResults.map((produto) => (
                    <button
                      key={produto.id}
                      type="button"
                      onClick={() => handleAddProduto(produto)}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center justify-between"
                      data-testid={`produto-option-${produto.id}`}
                    >
                      <span className="font-medium text-slate-900">{produto.nome}</span>
                      <Plus className="h-4 w-4 text-amber-600" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Produtos List */}
            {produtos.length === 0 ? (
              <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                <p>Nenhum produto adicionado</p>
                <p className="text-sm">Pesquise e adicione produtos acima</p>
              </div>
            ) : (
              <div className="space-y-4">
                {produtos.map((produto, index) => (
                  <div 
                    key={index} 
                    className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                    data-testid={`produto-item-${index}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-medium text-slate-900">{produto.produto_nome}</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveProduto(index)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 -mt-1 -mr-1"
                        data-testid={`remove-produto-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm text-slate-600">Quantidade</Label>
                        <Input
                          type="number"
                          min="1"
                          value={produto.quantidade}
                          onChange={(e) => handleUpdateProduto(index, 'quantidade', e.target.value)}
                          data-testid={`produto-quantidade-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-slate-600">Observação</Label>
                        <Input
                          value={produto.observacao || ''}
                          onChange={(e) => handleUpdateProduto(index, 'observacao', e.target.value)}
                          placeholder="Ex: Tomar após refeições"
                          data-testid={`produto-observacao-${index}`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Observações Internas */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900">
              Observações Internas
            </CardTitle>
            <p className="text-sm text-slate-500">
              Estas observações são apenas para uso interno e não aparecem nos documentos impressos.
            </p>
          </CardHeader>
          <CardContent>
            <Textarea
              value={observacoesInternas}
              onChange={(e) => setObservacoesInternas(e.target.value)}
              placeholder="Notas internas sobre esta receita..."
              rows={4}
              data-testid="receita-observacoes-internas"
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center gap-4">
          <Button
            type="submit"
            className="bg-teal-600 hover:bg-teal-700 text-white"
            disabled={loading}
            data-testid="receita-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                A guardar...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEdit ? 'Atualizar' : 'Criar'} Receita
              </>
            )}
          </Button>
          <Link to="/receitas">
            <Button type="button" variant="outline">
              Cancelar
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
};
